import { enqueueSnackbar } from "notistack";
import { useStore } from "../../store";
import { ServicePaginatedReturn, ServicePaginatedReturnData, ServiceReturn } from "../../types/ServiceResponse";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { isUserFacingSuccessMessage } from "./apiMessages";
import { buildSessionExpiredLoginUrl } from "../../services/authRedirectServices";

const fiveHundredMs = 500;

type ApiCallOptions = {
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
};

export function useApi() {
  const navigate = useNavigate();

  const {
    user,
    logout,
    openLoading,
    closeLoading,
  } = useStore();

  const sessionExpired = useCallback(() => {
    const loginUrl = buildSessionExpiredLoginUrl();

    logout();

    navigate( loginUrl, { replace: true });
  }, [ logout, navigate ]);

  const simpleApiCall = useCallback(async function simpleApiCall<Req, Res>(
    callback: ( payload: Req, token?: string ) => Promise<ServiceReturn<Res>>,
    payload: Req,
    toggleLoading?: () => void,
    options?: ApiCallOptions,
  ): Promise<Res | null> {
    toggleLoading
      ? toggleLoading()
      : openLoading();

    const res = await callback( payload, user?.token );

    setTimeout(() => {
      toggleLoading
        ? toggleLoading()
        : closeLoading();
    }, fiveHundredMs );

    if( res.data !== false ) {
      if( isUserFacingSuccessMessage( res.message ) && options?.showSuccessMessage !== false ) {
        enqueueSnackbar( res.message, {
          variant: res.data ? 'success' : 'error',
        });
      }

      return res.data as Res;
    } else {
      if( options?.showErrorMessage !== false ) {
        enqueueSnackbar( res.message, {
          variant: res.data ? 'success' : 'error',
        });
      }

      if(res.message.includes("Sessão Expirada")) sessionExpired();
    }

    return null;
  }, [ closeLoading, openLoading, sessionExpired, user?.token ]);

  const paginatedApiCall = useCallback(async function paginatedApiCall<Req, Res>(
    callback: ( payload: Req, token?: string ) => Promise<ServicePaginatedReturn<Res>>,
    payload: Req,
    toggleLoading?: () => void,
    options?: ApiCallOptions,
  ): Promise<ServicePaginatedReturnData<Res> | null> {
    toggleLoading
      ? toggleLoading()
      : openLoading();

    const res = await callback( payload, user?.token );

    setTimeout(() => {
      toggleLoading
        ? toggleLoading()
        : closeLoading();
    }, fiveHundredMs );

    if( res.data !== false ) {
      return res.data as ServicePaginatedReturnData<Res>;
    } else {
      if( options?.showErrorMessage !== false ) {
        enqueueSnackbar( res.message, {
          variant: res.data ? 'success' : 'error',
        });
      }

      if(res.message.includes("Sessão Expirada")) sessionExpired();
    }

    return null;
  }, [ closeLoading, openLoading, sessionExpired, user?.token ]);

  return useMemo(() => ({
    simpleApiCall,
    paginatedApiCall,
  }), [ simpleApiCall, paginatedApiCall ]);
};
